import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Nav from "@/components/Nav";
import { CATEGORY_BY_SLUG } from "@/data/work";

const EASE = [0.22, 1, 0.36, 1];

const titleCase = (s) =>
  s.replace(/(^|[\s-])([a-z])/g, (m, p, c) => p + c.toUpperCase());

export default function ProjectDetail() {
  const { slug, projectId } = useParams();
  const category = CATEGORY_BY_SLUG[slug];
  const event = category?.events.find((e) => e.id === projectId);

  const [active, setActive] = useState(0);
  const stripRef = useRef(null);
  const didMountRef = useRef(false);
  const galleryRef = useRef(0);

  useEffect(() => {
    galleryRef.current = event?.detail?.gallery?.length ?? 0;
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    setActive(0);
    didMountRef.current = false;
  }, [slug, projectId]);

  // Keep the active thumbnail visible in the strip — only after the user
  // actually changes the photo, never on mount (it would pull the page down)
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    const strip = stripRef.current;
    const thumb = strip?.querySelector(`[data-testid="album-thumb-${active}"]`);
    thumb?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [active]);

  // Left/right arrow keys step through the album
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      if (e.target.closest("input, textarea, select, [contenteditable]")) return;
      setActive((a) => {
        const len = galleryRef.current;
        if (len < 2) return a;
        return (a + (e.key === "ArrowRight" ? 1 : -1) + len) % len;
      });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!category || !event?.detail)
    return <Navigate to={category ? `/work/${slug}` : "/"} replace />;

  const d = event.detail;
  const gallery = d.gallery;
  const current = gallery[Math.min(active, gallery.length - 1)];
  // The brand logo already carries the brand name — only show the text
  // heading when the event name adds something different.
  const showName = d.name && d.name !== d.brand;

  const go = (dir) => setActive((a) => (a + dir + gallery.length) % gallery.length);

  return (
    <div
      data-testid="project-detail-page"
      className="bg-[color:var(--collab-cream)]"
    >
      <Nav />
      <main className="pt-20 md:pt-24 pb-10">
        {/* Breadcrumb — back link sits on the left */}
        <section className="shell">
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:justify-between">
            <Link
              to={`/work/${slug}`}
              className="label-mono hover:text-[color:var(--collab-forest)] transition-colors"
              data-testid="back-to-category"
            >
              ← {category.label}
            </Link>
          </div>
        </section>

        {/* Hero — two-column editorial spread: brand identity left,
            compact description + framed featured image right.
            Composed to fit within one viewport */}
        <section className="shell mt-8 md:mt-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-8 md:items-center">
            {/* Left: logo + event information */}
            <motion.div
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE }}
              className="md:col-span-5 text-left md:pr-20"
            >
              <img
                src={event.logo}
                alt={event.logoAlt}
                data-testid="project-logo"
                className={`${event.detailLogoClass ?? "h-[3.375rem] md:h-[4.125rem]"} w-auto object-contain select-none`}
                draggable="false"
              />
              {showName && (
                <h1
                  data-testid="project-name"
                  className="mt-6 font-display font-light text-4xl md:text-5xl leading-[1.05] tracking-[-0.02em] text-[color:var(--collab-ink)] balance"
                >
                  {titleCase(d.name)}
                </h1>
              )}
              {d.subtitle && (
                <p
                  data-testid="project-subtitle"
                  className={`${
                    showName ? "mt-3" : "mt-5 md:mt-6"
                  } font-display italic text-2xl md:text-3xl leading-[1.25] text-[color:var(--collab-forest)] balance`}
                >
                  {titleCase(d.subtitle)}
                </p>
              )}
              {d.description && (
                <p
                  data-testid="project-description"
                  className="mt-3 label-mono !text-[color:var(--collab-muted)]"
                >
                  {d.description}
                </p>
              )}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
                data-testid="project-content"
                className="mt-4 md:mt-5 text-sm md:text-base leading-[1.75] text-[color:var(--collab-muted)] pretty"
              >
                {event.copy}
              </motion.p>
            </motion.div>

            {/* Right: featured image in green frame */}
            <div className="md:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: EASE, delay: 0.25 }}
                data-testid="featured-section"
              >
                {/* Dark-green frame around the full photograph (echoes the
                    Our People ring treatment) — hugs the image, never crops */}
                <div className="relative w-fit max-w-full mx-auto border-[3px] border-[color:var(--collab-forest)] p-2 md:p-2.5">
                  <AnimatePresence initial={false} mode="popLayout">
                    {current.type === "video" ? (
                      <motion.video
                        key={current.src}
                        src={current.src}
                        poster={current.poster}
                        data-testid="featured-video"
                        autoPlay
                        muted
                        loop
                        playsInline
                        controls
                        preload="metadata"
                        initial={{ opacity: 0, scale: 1.01 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.55, ease: EASE }}
                        className="block w-auto max-w-full max-h-[60vh] md:max-h-[70vh] object-contain object-center select-none"
                      />
                    ) : (
                      <motion.img
                        key={current.src}
                        src={current.src}
                        alt={current.alt}
                        data-testid="featured-image"
                        loading="eager"
                        fetchPriority="high"
                        initial={{ opacity: 0, scale: 1.01 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.55, ease: EASE }}
                        className="block w-auto max-w-full max-h-[60vh] md:max-h-[70vh] object-contain object-center select-none"
                        draggable="false"
                      />
                    )}
                  </AnimatePresence>

                  {/* Prev / next arrows flanking the featured image
                      (hidden when the album has a single photo) */}
                  {gallery.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={() => go(-1)}
                        data-testid="featured-prev"
                        aria-label="Previous photo"
                        className="absolute -left-4 md:-left-14 top-1/2 -translate-y-1/2 z-10 w-11 h-11 md:w-12 md:h-12 rounded-full border border-[color:var(--collab-hairline)] bg-[color:var(--collab-cream)] flex items-center justify-center hover:bg-[color:var(--collab-ink)] hover:text-[color:var(--collab-cream)] hover:border-[color:var(--collab-ink)] transition-colors"
                      >
                        ←
                      </button>
                      <button
                        type="button"
                        onClick={() => go(1)}
                        data-testid="featured-next"
                        aria-label="Next photo"
                        className="absolute -right-4 md:-right-14 top-1/2 -translate-y-1/2 z-10 w-11 h-11 md:w-12 md:h-12 rounded-full border border-[color:var(--collab-hairline)] bg-[color:var(--collab-cream)] flex items-center justify-center hover:bg-[color:var(--collab-ink)] hover:text-[color:var(--collab-cream)] hover:border-[color:var(--collab-ink)] transition-colors"
                      >
                        →
                      </button>
                    </>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Album strip — horizontal filmstrip of all event photos.
            No label, no extra controls; the featured arrows drive navigation.
            Hidden when the album holds a single photo */}
        {gallery.length > 1 && (
          <section className="shell mt-5 md:mt-6" data-testid="album-section">
          <div
            ref={stripRef}
            data-testid="album-strip"
            className="flex gap-4 md:gap-5 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-1 w-fit max-w-full mx-auto"
          >
            {gallery.map((img, i) => {
              const isActive = i === active;
              return (
                <button
                  key={img.src}
                  type="button"
                  onClick={() => setActive(i)}
                  data-testid={`album-thumb-${i}`}
                  aria-label={`View photo ${i + 1}: ${img.alt}`}
                  aria-pressed={isActive}
                  className={`group relative shrink-0 snap-start h-12 md:h-[4.2rem] overflow-hidden transition-[opacity,box-shadow] duration-300 ${
                    isActive
                      ? "opacity-100 ring-2 ring-[color:var(--collab-forest)] ring-offset-2 ring-offset-[color:var(--collab-cream)]"
                      : "opacity-60 hover:opacity-100"
                  }`}
                >
                  {img.type === "video" && !img.poster ? (
                    <video
                      src={img.src}
                      muted
                      playsInline
                      preload="metadata"
                      aria-label={img.alt}
                      className="h-full w-auto object-cover select-none transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  ) : (
                    <img
                      src={img.poster ?? img.src}
                      alt={img.alt}
                      loading="lazy"
                      className="h-full w-auto object-cover select-none transition-transform duration-700 ease-out group-hover:scale-105"
                      draggable="false"
                    />
                  )}
                </button>
              );
            })}
          </div>
          </section>
        )}

        {/* The page intentionally ends after the album — one continuous
            project story, no further navigation or CTA */}
      </main>
    </div>
  );
}
